import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Task, TaskKindEnum, TaskStatusEnum } from 'graphql-common';
import _get from 'lodash/get';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import UserChip from '@lib/components/UserChip/UserChip';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import AssetPreview from '@lib/components/AssetPreview/AssetPreview';
import CircleLoaderWrapper, {
  CircleLoaderWrapperType,
} from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import CountMark, {
  CountMarkSizes,
  CountMarkTypes,
} from '@lib/components/CountMark/CountMark';
import { getPriorityChipLabel, getPriorityChipType } from '@lib/utils/priority';
import { APP_URLS } from 'constants/urls';
import TaskStatusChip from '@lib/components/TaskStatusChip/TaskStatusChip';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';

interface Props {
  currentUserId?: string;
  isViewOnly?: boolean;
  onStatusChangeHandler?: (id: string, v: TaskStatusEnum) => void;
  taskData?: Task;
  taskLoading?: boolean;
}

type InfoItemProps = {
  icon: string;
  label: string;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
};

function InfoItem({
  icon,
  label,
  children,
  className,
  contentClassName,
}: InfoItemProps) {
  return (
    <div className={classNames(entityViewStyles.infoItem, className)}>
      <p className={entityViewStyles.infoItemLabel}>
        <MaterialIcon icon={icon} symbolsOutlined />
        <Typography variant="caption">{label}</Typography>
      </p>
      <div
        className={classNames(
          entityViewStyles.infoItemContent,
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

const EMPTY_VALUE = <Typography variant="body">-</Typography>;

function DateValue({ value }: { value?: unknown }) {
  return (
    <Typography variant="body">
      {value ? getFormattedDate(value as string) : '-'}
    </Typography>
  );
}

function TaskReportingSidebar(props: Props) {
  const {
    currentUserId,
    isViewOnly,
    onStatusChangeHandler,
    taskData,
    taskLoading,
  } = props;
  const { t } = useTranslation();
  const {
    approver,
    archivedAt,
    asset,
    completedAt,
    deadlineAt,
    description,
    kind,
    lastCheckAt,
    lastDeadlineAt,
    lateFor,
    originalCreatedAt,
    priority,
    site,
    siteArea,
    status,
  } = taskData || {};

  const isOnDemand = kind === TaskKindEnum.OnDemand;
  const isCompleted = status === TaskStatusEnum.Completed;
  const isArchived = status === TaskStatusEnum.Archived;
  const canChangeStatus =
    !isViewOnly &&
    approver?.id === currentUserId &&
    !isCompleted &&
    !isArchived;

  return (
    <CircleLoaderWrapper
      isLoading={!!taskLoading}
      type={CircleLoaderWrapperType.blur}
    >
      <div>
        <InfoItem icon="segment" label={t('priority')}>
          {priority ? (
            <Chip
              type={getPriorityChipType(priority)}
              label={getPriorityChipLabel(priority)}
              leftIcon="circle"
            />
          ) : (
            EMPTY_VALUE
          )}
        </InfoItem>
        <InfoItem icon="track_changes" label={t('status')}>
          {status && taskData ? (
            <TaskStatusChip
              task={taskData}
              onStatusChangeHandler={
                canChangeStatus ? onStatusChangeHandler : undefined
              }
            />
          ) : (
            EMPTY_VALUE
          )}
        </InfoItem>
        <InfoItem
          icon="home_pin"
          label={t('site')}
          contentClassName={entityViewStyles.infoItemContentRow}
        >
          {site ? (
            <Chip type={ChipTypes.chipGray} label={_get(site, 'name')} />
          ) : (
            EMPTY_VALUE
          )}
        </InfoItem>
        <InfoItem
          icon="location_on"
          label={t('area')}
          contentClassName={entityViewStyles.infoItemContentRow}
        >
          {siteArea ? (
            <Chip type={ChipTypes.chipGray} label={_get(siteArea, 'name')} />
          ) : (
            EMPTY_VALUE
          )}
        </InfoItem>
        <InfoItem icon="inventory" label={t('asset')}>
          {asset ? (
            <AssetPreview
              url={APP_URLS.dashboard.assets.view.getDynamicPath({
                pathParts: { id: `${asset.id}` },
              })}
              name={asset.name}
              code={asset?.fullCode}
              icon={
                asset.iconAttached?.urls?.small ||
                asset.iconAttached?.urls?.original ||
                asset.iconAttached?.fileTag
              }
            />
          ) : (
            EMPTY_VALUE
          )}
        </InfoItem>
        <InfoItem icon="calendar_today" label={t('date-created')}>
          <DateValue value={originalCreatedAt} />
        </InfoItem>
        {isOnDemand ? (
          <InfoItem icon="history" label={t('last-completion')}>
            <DateValue value={isCompleted ? completedAt : lastCheckAt} />
          </InfoItem>
        ) : (
          <InfoItem icon="history" label={t('last-task-order')}>
            <DateValue value={lastDeadlineAt} />
          </InfoItem>
        )}
        {isCompleted && !isOnDemand && (
          <InfoItem icon="assignment_turned_in" label={t('completion-date')}>
            <DateValue value={completedAt} />
          </InfoItem>
        )}
        {!isOnDemand && !isCompleted && !isArchived && (
          <InfoItem icon="update" label={t('next-task-order')}>
            <DateValue value={deadlineAt} />
          </InfoItem>
        )}
        {lateFor && !isCompleted && !isArchived && (
          <InfoItem icon="clock_loader_20" label={t('late-for')}>
            <CountMark
              value={`${lateFor} ${t('day-short')}`}
              type={CountMarkTypes.red}
              size={CountMarkSizes.large}
            />
          </InfoItem>
        )}
        {isArchived && (
          <InfoItem icon="archive" label={t('archiving-date')}>
            <DateValue value={archivedAt} />
          </InfoItem>
        )}
        <InfoItem
          icon="how_to_reg"
          label={t('approver')}
          className={classNames({
            [entityViewStyles.infoItemCentered]: !approver,
          })}
          contentClassName={entityViewStyles.infoItemContentFullWidth}
        >
          {approver ? <UserChip user={approver} /> : EMPTY_VALUE}
        </InfoItem>
        {hasTextEditorValue(description) && (
          <InfoItem
            icon="description"
            label={t('description')}
            className={entityViewStyles.block}
          >
            <TextEditor
              readOnly
              value={description}
              className={entityViewStyles.textEditor}
            />
          </InfoItem>
        )}
      </div>
    </CircleLoaderWrapper>
  );
}

export default TaskReportingSidebar;
